import { signal, effect } from '@preact/signals';
import { secureStorage } from '../storage/secureStorage.js';

export type FontSize = 'sm' | 'md' | 'lg' | 'xl';
export type LineSpacing = 'tight' | 'normal' | 'loose';

export const FONT_SIZES: readonly FontSize[] = ['sm', 'md', 'lg', 'xl'];
export const LINE_SPACINGS: readonly LineSpacing[] = ['tight', 'normal', 'loose'];

const FONT_PX: Record<FontSize, string> = {
  sm: '15px',
  md: '16px',
  lg: '19px',
  xl: '22px',
};

const LINE_HEIGHT: Record<LineSpacing, string> = {
  tight: '1.45',
  normal: '1.65',
  loose: '1.85',
};

const STORAGE_KEY = '_rf2';

type Prefs = { fontSize: FontSize; lineSpacing: LineSpacing; literacyMode: boolean; paragraphMode: boolean };
const DEFAULTS: Prefs = { fontSize: 'md', lineSpacing: 'normal', literacyMode: false, paragraphMode: false };

function load(): Prefs {
  if (typeof localStorage === 'undefined') return DEFAULTS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<Prefs>;
    const fontSize = FONT_SIZES.includes(parsed.fontSize as FontSize)
      ? (parsed.fontSize as FontSize)
      : DEFAULTS.fontSize;
    const lineSpacing = LINE_SPACINGS.includes(parsed.lineSpacing as LineSpacing)
      ? (parsed.lineSpacing as LineSpacing)
      : DEFAULTS.lineSpacing;
    const literacyMode = typeof parsed.literacyMode === 'boolean'
      ? parsed.literacyMode
      : DEFAULTS.literacyMode;
    const paragraphMode = typeof parsed.paragraphMode === 'boolean'
      ? parsed.paragraphMode
      : DEFAULTS.paragraphMode;
    return { fontSize, lineSpacing, literacyMode, paragraphMode };
  } catch {
    return DEFAULTS;
  }
}

export const readingPrefs = signal<Prefs>(load());

if (typeof window !== 'undefined') {
  effect(() => {
    void secureStorage.setItem(STORAGE_KEY, JSON.stringify(readingPrefs.value));
    // Apply to <html> as CSS variables so the verse text scales globally.
    const root = document.documentElement;
    root.style.setProperty('--verse-font-size', FONT_PX[readingPrefs.value.fontSize]);
    root.style.setProperty('--verse-line-height', LINE_HEIGHT[readingPrefs.value.lineSpacing]);
  });
}

export function setFontSize(s: FontSize): void {
  readingPrefs.value = { ...readingPrefs.value, fontSize: s };
}

export function setLineSpacing(s: LineSpacing): void {
  readingPrefs.value = { ...readingPrefs.value, lineSpacing: s };
}

export function setLiteracyMode(on: boolean): void {
  readingPrefs.value = { ...readingPrefs.value, literacyMode: on };
}

export function setParagraphMode(on: boolean): void {
  readingPrefs.value = { ...readingPrefs.value, paragraphMode: on };
}

export const FONT_SIZE_LABELS: Record<FontSize, string> = { sm: 'S', md: 'M', lg: 'L', xl: 'XL' };
export const LINE_SPACING_LABELS: Record<LineSpacing, string> = {
  tight: 'Tight',
  normal: 'Normal',
  loose: 'Loose',
};
